import { RiAlertLine, RiShoppingCartLine } from "@remixicon/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { fetchClient } from "@/api/client";
import { OrderProgressCells, STEP_LABELS } from "@/components/orders/order-progress";
import { OrderTotal } from "@/components/orders/order-total";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatBerlin } from "@/lib/datetime";

export const Route = createFileRoute("/_app/orders/")({
	component: OrdersListPage,
});

interface OrderSummary {
	id: string;
	// CSV-only fields: NULL on EVENTS-sourced orders (migration 041 relaxed
	// these NOT-NULL constraints - see backend/src/shapes/farfetch_orders.rs).
	orderNumber: string | null;
	orderCode: string;
	siteId: string | null;
	prohandelState: string;
	branchNumber: number | null;
	fulfillmentStatus: string;
	step: number | null;
	status: number | null;
	customerNickname: string | null;
	totalWithShip: string | null;
	currency: string | null;
	dateCreated: string;
	// Progress-step timestamps, all derived server-side from existing data
	// rather than a status enum that could drift - see OrderSummaryShape.
	allocatedAt: string | null;
	reservedAt: string | null;
	notifiedAt: string | null;
	inTransferBranchAt: string | null;
	/** Total converted to EUR; null when no rate is available. */
	totalEur: number | null;
	/** Prohandel booked the sale out of the transfer branch - the final step. */
	issuedAt: string | null;
	transferNotApplicable: boolean;
}

interface OrdersResponse {
	data: OrderSummary[];
	total: number;
	limit: number;
	offset: number;
}

/** Held reservations the sweep found short of stock (DEV-812). */
interface StockAlerts {
	orders: number;
	reservations: number;
}

const STATUS_FILTERS = [
	{ value: "all", label: "All statuses" },
	{ value: "pending", label: "Pending" },
	{ value: "needs_branch", label: "Needs branch" },
	{ value: "no_single_branch", label: "No single branch" },
	{ value: "partial_stock", label: "Partial stock" },
	{ value: "ready", label: "Ready" },
	{ value: "shipped", label: "Shipped" },
	{ value: "cancelled", label: "Cancelled" },
];

/// How often the orders list re-fetches. 30s is well inside the window in which
/// an operator would otherwise notice a new order, while being ~120 requests an
/// hour per open tab against a list endpoint that is already paginated.
const ORDERS_POLL_MS = 30_000;

function OrdersListPage() {
	const [statusFilter, setStatusFilter] = useState("all");
	const navigate = useNavigate();

	const query = useQuery({
		queryKey: ["farfetch", "orders", statusFilter],
		queryFn: async () => {
			const params =
				statusFilter === "all"
					? undefined
					: { params: { query: { fulfillmentStatus: statusFilter } } };
			const { data, error } = await fetchClient.GET(
				"/api/v1/farfetch/orders" as never,
				params as never,
			);
			if (error) throw new Error("failed to load orders");
			return data as OrdersResponse;
		},
		// Orders arrive by Farfetch webhook at any time, so a static list goes
		// stale silently and an operator only finds out by reloading. Poll so a
		// new order shows up on its own.
		//
		// refetchIntervalInBackground stays false (the default): a hidden tab
		// must not keep polling, and TanStack refetches on window focus anyway,
		// so returning to the tab shows current data immediately.
		refetchInterval: ORDERS_POLL_MS,
	});

	const orders = query.data?.data ?? [];

	// Held reservations whose branch no longer has the stock. Written by the
	// 60s held-stock sweep; shown up here because it is invisible per row until
	// an order is opened, and an operator only opens orders they already suspect.
	const alerts = useQuery({
		queryKey: ["farfetch", "orders", "stock-alerts"],
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/api/v1/farfetch/orders/stock-alerts" as never,
			);
			if (error) throw new Error("failed to load stock alerts");
			return data as StockAlerts;
		},
		refetchInterval: ORDERS_POLL_MS,
	});
	const shortOrders = alerts.data?.orders ?? 0;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Orders</h1>
					<p className="text-muted-foreground">Farfetch orders awaiting fulfillment.</p>
				</div>
				{shortOrders > 0 && (
					<div
						role="status"
						data-testid="stock-alerts"
						className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-sm text-destructive"
					>
						<RiAlertLine className="size-4" aria-hidden="true" />
						<span>
							{shortOrders === 1
								? "1 order is short of stock at its branch"
								: `${shortOrders} orders are short of stock at their branch`}
						</span>
					</div>
				)}
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-48">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{STATUS_FILTERS.map((f) => (
							<SelectItem key={f.value} value={f.value}>
								{f.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{query.isLoading ? (
				<div data-testid="orders-skeleton" className="space-y-2">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
			) : query.error ? (
				<div
					role="alert"
					className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
				>
					<p className="font-medium">Could not load orders.</p>
					<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
						Retry
					</Button>
				</div>
			) : orders.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<RiShoppingCartLine className="size-12 text-muted-foreground/30 mb-3" />
					<p className="text-muted-foreground">No orders yet</p>
				</div>
			) : (
				<div className="border rounded-md overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Order</TableHead>
								{/* One column PER step, rather than six dots under a single
								    "Fortschritt" heading. The heading is then the label, so an
								    operator reads the meaning without hovering, and a column of
								    gaps shows at a glance where orders are piling up. */}
								{STEP_LABELS.map((label) => (
									<TableHead key={label} className="px-2 text-center text-xs font-normal">
										{label}
									</TableHead>
								))}

								<TableHead>Branch</TableHead>
								<TableHead>Customer</TableHead>
								<TableHead className="text-right">Total</TableHead>
								<TableHead>Erstellt</TableHead>
								<TableHead className="w-16 text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{orders.map((o) => (
								<TableRow
									key={o.id}
									// The whole row is the target, not just the View button - the
									// button stays because people look for it, but hitting a 900px
									// row is easier than hitting a 40px link. role/tabIndex/keydown
									// keep it reachable without a mouse; a bare onClick would make
									// the row invisible to keyboard and screen-reader users.
									role="link"
									tabIndex={0}
									aria-label={`Bestellung ${o.orderNumber ?? o.orderCode} öffnen`}
									className="cursor-pointer"
									onClick={() => navigate({ to: "/orders/$orderId", params: { orderId: o.id } })}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											navigate({ to: "/orders/$orderId", params: { orderId: o.id } });
										}
									}}
								>
									{/* Farfetch's own order number (merchantOrderCode, e.g.
									    BRS189179599) - the reference Burresi and Farfetch support both
									    quote. `orderCode` (SO-6JZYR3) is internal to the ShippingOrder
									    and means nothing to either party, so it is the fallback, not
									    the headline. */}
									<TableCell className="font-mono text-xs">
										{o.orderNumber ?? o.orderCode}
									</TableCell>
									{OrderProgressCells({
										dateCreated: o.dateCreated,
										allocatedAt: o.allocatedAt,
										reservedAt: o.reservedAt,
										notifiedAt: o.notifiedAt,
										inTransferBranchAt: o.inTransferBranchAt,
										issuedAt: o.issuedAt,
										transferNotApplicable: o.transferNotApplicable,
										fulfillmentStatus: o.fulfillmentStatus,
									}).map((cell) => (
										// The cell already carries the step's own key, so the wrapper
										// reuses it rather than falling back to the array index.
										<TableCell key={cell.key} className="px-2">
											<div className="flex justify-center">{cell}</div>
										</TableCell>
									))}
									{/* A split order lists several branches here, so it needs no
									    separate "split" badge - the column already says so. */}
									<TableCell>{o.branchNumber ?? "-"}</TableCell>
									<TableCell className="text-muted-foreground">
										{o.customerNickname || "-"}
									</TableCell>
									<TableCell className="text-right">
										<OrderTotal
											totalWithShip={o.totalWithShip}
											currency={o.currency}
											totalEur={o.totalEur}
										/>
									</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										{formatBerlin(o.dateCreated)}
									</TableCell>
									<TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
										<Button asChild variant="ghost" size="sm">
											<Link to="/orders/$orderId" params={{ orderId: o.id }}>
												View
											</Link>
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
